import { useEffect, useState } from 'react';
import { finApi } from '../../api/finance.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Payments() {
  const { can } = useAuth();
  const canCreate = can('payment', 'create');

  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  function refresh() { finApi.payments().then((d) => setItems(d?.items || [])); }
  useEffect(() => {
    refresh();
    finApi.vendors({ active: true }).then((d) => setVendors(d?.items || []));
    finApi.customers({ active: true }).then((d) => setCustomers(d?.items || []));
    finApi.bankAccounts().then((d) => setBankAccounts(d?.items || []));
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    try {
      await finApi.recordPayment({
        type: fd.get('type'),
        referenceId: fd.get('referenceId'),
        amount: Number(fd.get('amount')),
        paymentDate: fd.get('paymentDate'),
        method: fd.get('method'),
        bankAccountId: fd.get('bankAccountId') || null,
        reference: fd.get('reference') || null,
        notes: fd.get('notes') || null,
      });
      e.currentTarget.reset();
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Payments</h1>
        {canCreate && <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ Record Payment'}</button>}
      </header>

      {error && <div className="alert error">{error}</div>}

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <PaymentForm vendors={vendors} customers={customers} bankAccounts={bankAccounts} />
          <div className="full actions">
            <button type="submit" className="btn primary">Record</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table">
          <thead><tr><th>#</th><th>Type</th><th>Date</th><th>Amount</th><th>Method</th><th>Bank</th><th>Reference</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td><code>{p.payment_no}</code></td>
                <td><span className="chip">{p.type}</span></td>
                <td>{p.payment_date}</td>
                <td>{p.currency} {Number(p.amount).toFixed(2)}</td>
                <td className="muted">{p.method}</td>
                <td>{p.bank_name ? `${p.bank_name} ${p.account_no}` : '—'}</td>
                <td className="muted">{p.reference || '—'}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="muted">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <AgingPanel />
    </section>
  );
}

function PaymentForm({ vendors, customers, bankAccounts }) {
  const [type, setType] = useState('payable');
  const parties = type === 'payable' ? vendors : customers;
  return (
    <>
      <label>
        Type
        <select name="type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="payable">Payable (to vendor)</option>
          <option value="receivable">Receivable (from customer)</option>
        </select>
      </label>
      <label>
        {type === 'payable' ? 'Vendor' : 'Customer'}
        <select name="referenceId" required defaultValue="">
          <option value="" disabled>Select…</option>
          {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label>Amount<input name="amount" type="number" step="0.01" required /></label>
      <label>Date<input name="paymentDate" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required /></label>
      <label>
        Method
        <select name="method" defaultValue="wire">
          <option value="cash">Cash</option><option value="check">Check</option>
          <option value="wire">Wire</option><option value="ach">ACH</option>
          <option value="card">Card</option><option value="other">Other</option>
        </select>
      </label>
      <label>
        Bank Account
        <select name="bankAccountId" defaultValue="">
          <option value="">—</option>
          {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.bank_name} · {b.account_no}</option>)}
        </select>
      </label>
      <label className="full">Reference<input name="reference" placeholder="check #, txn id, etc." /></label>
      <label className="full">Notes<input name="notes" /></label>
    </>
  );
}

function AgingPanel() {
  const [type, setType] = useState('receivable');
  const [data, setData] = useState(null);

  useEffect(() => {
    finApi.aging(type).then(setData).catch(() => setData(null));
  }, [type]);

  return (
    <div className="card">
      <header className="page-header">
        <h3>Aging — {type === 'receivable' ? 'Receivables' : 'Payables'}</h3>
        <div className="seg">
          <button className={type === 'receivable' ? 'active' : ''} onClick={() => setType('receivable')}>Receivables</button>
          <button className={type === 'payable' ? 'active' : ''} onClick={() => setType('payable')}>Payables</button>
        </div>
      </header>
      {!data ? <div className="muted">Loading…</div> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{type === 'receivable' ? 'Customer' : 'Vendor'}</th>
              <th>Current</th><th>1–30</th><th>31–60</th><th>61–90</th><th>90+</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.parties.map((p) => (
              <tr key={p.partyId}>
                <td><strong>{p.partyName}</strong></td>
                <td>{p.current.toFixed(2)}</td>
                <td>{p['1-30'].toFixed(2)}</td>
                <td>{p['31-60'].toFixed(2)}</td>
                <td className={p['61-90'] > 0 ? 'warn' : ''}>{p['61-90'].toFixed(2)}</td>
                <td className={p['90+'] > 0 ? 'danger' : ''}>{p['90+'].toFixed(2)}</td>
                <td><strong>{p.total.toFixed(2)}</strong></td>
              </tr>
            ))}
            {data.parties.length === 0 && <tr><td colSpan={7} className="muted">Nothing outstanding.</td></tr>}
            {data.parties.length > 0 && (
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>{data.totals.current.toFixed(2)}</strong></td>
                <td><strong>{data.totals['1-30'].toFixed(2)}</strong></td>
                <td><strong>{data.totals['31-60'].toFixed(2)}</strong></td>
                <td><strong>{data.totals['61-90'].toFixed(2)}</strong></td>
                <td><strong>{data.totals['90+'].toFixed(2)}</strong></td>
                <td><strong>{data.totals.total.toFixed(2)}</strong></td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
